export interface AssetTypeConfig {
  id: string;
  label: string;
  description: string;
  icon: string;
  category: 'text' | 'media' | 'ads';
  requiresUpload: boolean;
  mimeType: string;
  fields: AssetField[];
}

export interface AssetField {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'richtext' | 'select' | 'multiselect' | 'tags' | 'date' | 'time' | 'url' | 'email' | 'number' | 'json' | 'file';
  required: boolean;
  placeholder?: string;
  helpText?: string;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    min?: number;
    max?: number;
  };
  options?: { value: string; label: string }[];
  defaultValue?: any;
  conditional?: {
    field: string;
    value: any;
  };
  width?: 'full' | 'half' | 'third'; // Layout width hint (default: full)
}

export const assetTypes: AssetTypeConfig[] = [
  // Text-based content assets
  {
    id: 'copy',
    label: 'Copy/Text Content',
    description: 'Generic text content or copy',
    icon: 'FileText',
    category: 'text',
    requiresUpload: false,
    mimeType: 'text/plain',
    fields: [
      {
        name: 'title',
        label: 'Title',
        type: 'text',
        required: true,
        placeholder: 'Enter content title'
      },
      {
        name: 'content',
        label: 'Content',
        type: 'richtext',
        required: true,
        helpText: 'Main content body'
      },
      {
        name: 'images',
        label: 'Content Images',
        type: 'file',
        required: false,
        helpText: 'Upload images for hero, inline use, or featured image (JPG, PNG, WebP)',
        width: 'full'
      },
      {
        name: 'tags',
        label: 'Tags',
        type: 'tags',
        required: false,
        placeholder: 'Add tags...'
      },
      {
        name: 'category',
        label: 'Category',
        type: 'select',
        required: false,
        options: [
          { value: 'blog', label: 'Blog Post' },
          { value: 'article', label: 'Article' },
          { value: 'guide', label: 'Guide' },
          { value: 'whitepaper', label: 'Whitepaper' },
          { value: 'other', label: 'Other' }
        ]
      }
    ]
  },
  {
    id: 'email',
    label: 'Email Template',
    description: 'Email campaign template with A/B testing variations',
    icon: 'Mail',
    category: 'text',
    requiresUpload: false,
    mimeType: 'text/html',
    fields: [
      // Section 1: A/B Testing Variations
      {
        name: 'subject_variations',
        label: 'Subject Line Variations (up to 5)',
        type: 'tags',
        required: true,
        placeholder: 'Add subject lines for A/B testing (max 100 chars each)',
        validation: { maxLength: 100 },
        helpText: 'Platform will automatically test these variations. Add 2-5 subject lines.',
        width: 'full'
      },
      {
        name: 'preheader_variations',
        label: 'Preheader Text Variations (up to 3)',
        type: 'tags',
        required: false,
        placeholder: 'Add preheader variations (max 150 chars each)',
        validation: { maxLength: 150 },
        helpText: 'Test different preview text shown in inbox. Add 1-3 variations.',
        width: 'full'
      },

      // Section 2: Sender Information
      {
        name: 'from_name',
        label: 'From Name',
        type: 'text',
        required: true,
        placeholder: 'Sender name',
        width: 'half'
      },
      {
        name: 'from_email',
        label: 'From Email',
        type: 'email',
        required: true,
        placeholder: 'sender@example.com',
        width: 'half'
      },
      {
        name: 'reply_to',
        label: 'Reply-To Email',
        type: 'email',
        required: false,
        placeholder: 'reply@example.com',
        width: 'half'
      },
      {
        name: 'send_date',
        label: 'Send Date',
        type: 'date',
        required: false,
        width: 'half'
      },

      // Section 3: Email Content
      {
        name: 'html_content',
        label: 'HTML Content',
        type: 'richtext',
        required: true,
        helpText: 'Email body content',
        width: 'full'
      },
      {
        name: 'plain_text',
        label: 'Plain Text Version',
        type: 'textarea',
        required: false,
        helpText: 'Plain text fallback',
        width: 'full'
      },
      {
        name: 'images',
        label: 'Email Images',
        type: 'file',
        required: false,
        helpText: 'Upload images to include in email (JPG, PNG, GIF)',
        width: 'full'
      },

      // Section 4: Tracking & Analytics
      {
        name: 'utm_source',
        label: 'UTM Source',
        type: 'text',
        required: false,
        defaultValue: 'email',
        width: 'third'
      },
      {
        name: 'utm_medium',
        label: 'UTM Medium',
        type: 'text',
        required: false,
        defaultValue: 'email',
        width: 'third'
      },
      {
        name: 'utm_campaign',
        label: 'UTM Campaign',
        type: 'text',
        required: false,
        placeholder: 'campaign_name',
        width: 'third'
      }
    ]
  },
  {
    id: 'social',
    label: 'Social Media Post',
    description: 'Social media content with A/B testing variations and hashtag sets',
    icon: 'Share2',
    category: 'text',
    requiresUpload: false,
    mimeType: 'text/plain',
    fields: [
      // Section 1: Platform Selection
      {
        name: 'platform',
        label: 'Platform',
        type: 'select',
        required: true,
        options: [
          { value: 'facebook', label: 'Facebook (63,206 char limit)' },
          { value: 'twitter', label: 'Twitter/X (280 char limit)' },
          { value: 'linkedin', label: 'LinkedIn (3,000 char limit)' },
          { value: 'instagram', label: 'Instagram (2,200 char limit)' }
        ],
        width: 'full',
        helpText: 'Character limits will be enforced based on platform'
      },

      // Section 2: Post Copy Variations
      {
        name: 'post_text_variations',
        label: 'Post Copy Variations (up to 5)',
        type: 'tags',
        required: true,
        placeholder: 'Add different post variations for A/B testing',
        helpText: 'Platform will test which copy performs best. Add 2-5 variations.',
        width: 'full'
      },

      // Section 3: Hashtag Sets for Testing
      {
        name: 'hashtag_set_1',
        label: 'Hashtag Set #1',
        type: 'tags',
        required: false,
        placeholder: 'Add hashtags without # (e.g., marketing, digital, growth)',
        helpText: 'First hashtag set for testing',
        width: 'full'
      },
      {
        name: 'hashtag_set_2',
        label: 'Hashtag Set #2',
        type: 'tags',
        required: false,
        placeholder: 'Add alternative hashtags',
        helpText: 'Second hashtag set for testing',
        width: 'full'
      },
      {
        name: 'hashtag_set_3',
        label: 'Hashtag Set #3',
        type: 'tags',
        required: false,
        placeholder: 'Add another hashtag variation',
        helpText: 'Third hashtag set for testing',
        width: 'full'
      },

      // Section 4: Post Settings
      {
        name: 'mentions',
        label: '@Mentions',
        type: 'tags',
        required: false,
        placeholder: 'Add mentions (without @)',
        width: 'half'
      },
      {
        name: 'link',
        label: 'Link',
        type: 'url',
        required: false,
        placeholder: 'https://example.com',
        width: 'half'
      },
      {
        name: 'cta',
        label: 'Call to Action',
        type: 'text',
        required: false,
        placeholder: 'Learn More, Shop Now, etc.',
        width: 'half'
      },
      {
        name: 'images',
        label: 'Post Images',
        type: 'file',
        required: false,
        helpText: 'Upload images for your post (JPG, PNG)',
        width: 'half'
      },
      {
        name: 'schedule_date',
        label: 'Schedule Date',
        type: 'date',
        required: false,
        width: 'half'
      },
      {
        name: 'schedule_time',
        label: 'Schedule Time',
        type: 'time',
        required: false,
        width: 'half'
      }
    ]
  },
  {
    id: 'meta',
    label: 'SEO Meta Tags',
    description: 'Page metadata for SEO optimization',
    icon: 'Hash',
    category: 'text',
    requiresUpload: false,
    mimeType: 'application/json',
    fields: [
      {
        name: 'page_url',
        label: 'Page URL',
        type: 'url',
        required: true,
        placeholder: 'https://example.com/page'
      },
      {
        name: 'title',
        label: 'Page Title',
        type: 'text',
        required: true,
        placeholder: 'Page title (50-60 characters)',
        validation: { maxLength: 60 }
      },
      {
        name: 'description',
        label: 'Meta Description',
        type: 'textarea',
        required: true,
        placeholder: 'Page description (150-160 characters)',
        validation: { maxLength: 160 }
      },
      {
        name: 'keywords',
        label: 'Keywords',
        type: 'tags',
        required: false,
        placeholder: 'Add keywords...'
      },
      {
        name: 'canonical_url',
        label: 'Canonical URL',
        type: 'url',
        required: false,
        placeholder: 'https://example.com/canonical'
      },
      {
        name: 'og_title',
        label: 'OG Title',
        type: 'text',
        required: false,
        placeholder: 'Open Graph title'
      },
      {
        name: 'og_description',
        label: 'OG Description',
        type: 'textarea',
        required: false,
        placeholder: 'Open Graph description'
      },
      {
        name: 'og_image',
        label: 'OG Image URL',
        type: 'url',
        required: false,
        placeholder: 'https://example.com/image.jpg',
        width: 'half'
      },
      {
        name: 'og_image_file',
        label: 'Upload OG Image',
        type: 'file',
        required: false,
        helpText: 'Upload Open Graph image (1200x630px recommended, JPG/PNG)',
        width: 'half'
      },
      {
        name: 'robots',
        label: 'Robots Directive',
        type: 'select',
        required: false,
        options: [
          { value: 'index,follow', label: 'Index, Follow' },
          { value: 'noindex,follow', label: 'No Index, Follow' },
          { value: 'index,nofollow', label: 'Index, No Follow' },
          { value: 'noindex,nofollow', label: 'No Index, No Follow' }
        ],
        defaultValue: 'index,follow'
      }
    ]
  },
  {
    id: 'faq',
    label: 'FAQ Content',
    description: 'Frequently asked questions and answers',
    icon: 'HelpCircle',
    category: 'text',
    requiresUpload: false,
    mimeType: 'application/json',
    fields: [
      {
        name: 'category',
        label: 'FAQ Category',
        type: 'text',
        required: true,
        placeholder: 'e.g., General, Pricing, Support'
      },
      {
        name: 'questions',
        label: 'Questions & Answers',
        type: 'json',
        required: true,
        helpText: 'Add your FAQ items in JSON format'
      },
      {
        name: 'images',
        label: 'Supporting Images',
        type: 'file',
        required: false,
        helpText: 'Upload diagrams, screenshots, or illustrations (JPG, PNG)',
        width: 'full'
      },
      {
        name: 'schema_markup',
        label: 'Generate Schema Markup',
        type: 'select',
        required: false,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ],
        defaultValue: 'yes'
      },
      {
        name: 'format',
        label: 'Display Format',
        type: 'select',
        required: false,
        options: [
          { value: 'accordion', label: 'Accordion' },
          { value: 'list', label: 'List' },
          { value: 'grid', label: 'Grid' }
        ],
        defaultValue: 'accordion'
      }
    ]
  },

  // Ad campaign assets
  {
    id: 'google_ads',
    label: 'Google Ads',
    description: 'Search, Display, Shopping & Video campaigns',
    icon: 'Megaphone',
    category: 'ads',
    requiresUpload: false,
    mimeType: 'application/json',
    fields: [
      { name: 'campaign_name', label: 'Campaign Name', type: 'text', required: true, placeholder: 'Enter campaign name', width: 'half' },
      { name: 'campaign_type', label: 'Campaign Type', type: 'select', required: true, width: 'half', options: [
        { value: 'search', label: 'Search' }, { value: 'display', label: 'Display' }, { value: 'shopping', label: 'Shopping' },
        { value: 'video', label: 'Video' }, { value: 'performance_max', label: 'Performance Max' }, { value: 'discovery', label: 'Discovery' }
      ]},
      { name: 'saved_audience', label: 'Use Saved Audiences (Optional)', type: 'multiselect', required: false, helpText: 'Select one or more saved audiences to auto-fill targeting - criteria will be combined', width: 'full', options: [] },
      { name: 'headlines', label: 'Headlines (up to 15)', type: 'tags', required: true, placeholder: 'Max 30 chars each', validation: { maxLength: 30 } },
      { name: 'descriptions', label: 'Descriptions (up to 4)', type: 'tags', required: true, placeholder: 'Max 90 chars each', validation: { maxLength: 90 } },
      { name: 'ad_images', label: 'Ad Creative Images', type: 'file', required: false, helpText: 'Upload display banners, product images, or shopping feed images (JPG, PNG, GIF)', width: 'full' },
      { name: 'display_url', label: 'Display URL', type: 'text', required: true, placeholder: 'example.com/page', width: 'half' },
      { name: 'final_url', label: 'Final URL', type: 'url', required: true, placeholder: 'https://example.com/landing', width: 'half' },
      { name: 'path1', label: 'URL Path 1', type: 'text', required: false, placeholder: 'products', validation: { maxLength: 15 }, width: 'half' },
      { name: 'path2', label: 'URL Path 2', type: 'text', required: false, placeholder: 'sale', validation: { maxLength: 15 }, width: 'half' },
      { name: 'keywords', label: 'Target Keywords', type: 'tags', required: false, placeholder: 'Add keywords' },
      { name: 'negative_keywords', label: 'Negative Keywords', type: 'tags', required: false, placeholder: 'Keywords to exclude' },
      { name: 'budget', label: 'Budget Amount', type: 'number', required: true, validation: { min: 1 }, width: 'half' },
      { name: 'budget_type', label: 'Budget Type', type: 'select', required: true, width: 'half', options: [
        { value: 'daily', label: 'Daily' }, { value: 'total', label: 'Campaign Total' }, { value: 'monthly', label: 'Monthly' }
      ]},
      { name: 'bid_strategy', label: 'Bid Strategy', type: 'select', required: true, options: [
        { value: 'manual_cpc', label: 'Manual CPC' }, { value: 'enhanced_cpc', label: 'Enhanced CPC' }, { value: 'maximize_clicks', label: 'Maximize Clicks' },
        { value: 'target_cpa', label: 'Target CPA' }, { value: 'target_roas', label: 'Target ROAS' }, { value: 'maximize_conversions', label: 'Maximize Conversions' }
      ]},
      { name: 'start_date', label: 'Start Date', type: 'date', required: true, width: 'half' },
      { name: 'end_date', label: 'End Date', type: 'date', required: false, width: 'half' },
      { name: 'locations', label: 'Target Locations', type: 'tags', required: false, placeholder: 'Countries/cities', width: 'half' },
      { name: 'languages', label: 'Languages', type: 'tags', required: false, placeholder: 'Target languages', width: 'half' },
      { name: 'devices', label: 'Devices', type: 'multiselect', required: false, width: 'half', options: [
        { value: 'mobile', label: 'Mobile' }, { value: 'tablet', label: 'Tablet' }, { value: 'desktop', label: 'Desktop' }
      ]},
      { name: 'audiences', label: 'Target Audiences', type: 'tags', required: false, placeholder: 'Audience segments', width: 'half' },
      { name: 'ad_extensions_call', label: 'Call Extension Phone', type: 'text', required: false, placeholder: '+1-555-0100', width: 'half' },
      { name: 'ad_extensions_sitelinks', label: 'Sitelinks (JSON)', type: 'textarea', required: false,
        helpText: 'Format: [{"text":"Shop Now","url":"https://...","description":"..."}]' },
      { name: 'status', label: 'Status', type: 'select', required: false, width: 'half', options: [
        { value: 'draft', label: 'Draft' }, { value: 'pending', label: 'Pending' }, { value: 'active', label: 'Active' },
        { value: 'paused', label: 'Paused' }, { value: 'completed', label: 'Completed' }
      ], defaultValue: 'draft' }
    ]
  },
  {
    id: 'facebook_ads',
    label: 'Facebook & Instagram',
    description: 'Social media ads for Facebook, Instagram & Messenger',
    icon: 'Share2',
    category: 'ads',
    requiresUpload: false,
    mimeType: 'application/json',
    fields: [
      { name: 'campaign_name', label: 'Campaign Name', type: 'text', required: true, placeholder: 'Enter campaign name', width: 'half' },
      { name: 'objective', label: 'Campaign Objective', type: 'select', required: true, width: 'half', options: [
        { value: 'awareness', label: 'Brand Awareness' }, { value: 'traffic', label: 'Traffic' }, { value: 'engagement', label: 'Engagement' },
        { value: 'leads', label: 'Leads' }, { value: 'app_promotion', label: 'App Promotion' }, { value: 'sales', label: 'Sales/Conversions' }
      ]},
      { name: 'placement', label: 'Placements', type: 'multiselect', required: true, options: [
        { value: 'facebook_feed', label: 'Facebook Feed' }, { value: 'instagram_feed', label: 'Instagram Feed' },
        { value: 'facebook_stories', label: 'Facebook Stories' }, { value: 'instagram_stories', label: 'Instagram Stories' },
        { value: 'facebook_reels', label: 'Facebook Reels' }, { value: 'instagram_reels', label: 'Instagram Reels' },
        { value: 'messenger', label: 'Messenger' }, { value: 'audience_network', label: 'Audience Network' }
      ]},
      { name: 'saved_audience', label: 'Use Saved Audiences (Optional)', type: 'multiselect', required: false, helpText: 'Select one or more saved audiences to auto-fill targeting - criteria will be combined', width: 'full', options: [] },
      { name: 'primary_text_variations', label: 'Primary Text Variations (up to 5)', type: 'tags', required: true, placeholder: 'Main ad copy (max 125 chars)', validation: { maxLength: 125 },
        helpText: 'Facebook will test these variations automatically' },
      { name: 'headline_variations', label: 'Headline Variations (up to 5)', type: 'tags', required: true, placeholder: 'Ad headline (max 40 chars)', validation: { maxLength: 40 },
        helpText: 'Multiple headlines for A/B testing' },
      { name: 'description_variations', label: 'Description Variations (up to 5)', type: 'tags', required: false, placeholder: 'Link description (max 30 chars)', validation: { maxLength: 30 },
        helpText: 'Optional description variations' },
      { name: 'ad_images', label: 'Ad Creative Images', type: 'file', required: false, helpText: 'Upload feed images (1080x1080), stories (1080x1920), or carousel images (JPG, PNG)', width: 'full' },
      { name: 'ad_videos', label: 'Video Creatives', type: 'file', required: false, helpText: 'Upload video ads for feed, stories, or reels (MP4, MOV, max 4GB)', width: 'full' },
      { name: 'call_to_action', label: 'Call to Action', type: 'select', required: true, width: 'half', options: [
        { value: 'shop_now', label: 'Shop Now' }, { value: 'learn_more', label: 'Learn More' }, { value: 'sign_up', label: 'Sign Up' },
        { value: 'download', label: 'Download' }, { value: 'contact_us', label: 'Contact Us' }, { value: 'book_now', label: 'Book Now' },
        { value: 'get_offer', label: 'Get Offer' }
      ]},
      { name: 'link_url', label: 'Website URL', type: 'url', required: true, placeholder: 'https://your-site.com', width: 'half' },
      { name: 'budget', label: 'Budget Amount', type: 'number', required: true, validation: { min: 1 }, width: 'half' },
      { name: 'budget_type', label: 'Budget Type', type: 'select', required: true, width: 'half', options: [
        { value: 'daily', label: 'Daily' }, { value: 'lifetime', label: 'Lifetime' }
      ]},
      { name: 'bid_strategy', label: 'Bid Strategy', type: 'select', required: false, options: [
        { value: 'lowest_cost', label: 'Lowest Cost' }, { value: 'cost_cap', label: 'Cost Cap' }, { value: 'bid_cap', label: 'Bid Cap' },
        { value: 'target_cost', label: 'Target Cost' }, { value: 'highest_value', label: 'Highest Value' }, { value: 'minimum_roas', label: 'Minimum ROAS' }
      ]},
      { name: 'start_date', label: 'Start Date', type: 'date', required: true, width: 'half' },
      { name: 'end_date', label: 'End Date', type: 'date', required: false, width: 'half' },
      { name: 'age_min', label: 'Min Age', type: 'number', required: true, validation: { min: 18, max: 65 }, defaultValue: 18, width: 'half' },
      { name: 'age_max', label: 'Max Age', type: 'number', required: true, validation: { min: 18, max: 65 }, defaultValue: 65, width: 'half' },
      { name: 'gender', label: 'Gender', type: 'select', required: true, width: 'half', options: [
        { value: 'all', label: 'All Genders' }, { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }
      ], defaultValue: 'all' },
      { name: 'locations', label: 'Target Locations', type: 'tags', required: true, placeholder: 'Countries/cities', width: 'half' },
      { name: 'interests', label: 'Interests', type: 'tags', required: false, placeholder: 'Interest categories', width: 'half' },
      { name: 'behaviors', label: 'Behaviors', type: 'tags', required: false, placeholder: 'Behavioral targeting', width: 'half' },
      { name: 'custom_audiences', label: 'Custom Audiences', type: 'tags', required: false, placeholder: 'Audience IDs', width: 'half' },
      { name: 'lookalikes', label: 'Lookalike Audiences', type: 'tags', required: false, placeholder: 'Lookalike audience IDs', width: 'half' },
      { name: 'pixel_id', label: 'Facebook Pixel ID', type: 'text', required: false, placeholder: 'Your Pixel ID', width: 'half' },
      { name: 'carousel_cards', label: 'Carousel Cards (JSON)', type: 'textarea', required: false,
        helpText: 'Format: [{"image":"url","headline":"...","description":"...","link":"..."}]' },
      { name: 'status', label: 'Status', type: 'select', required: false, width: 'half', options: [
        { value: 'draft', label: 'Draft' }, { value: 'pending', label: 'Pending' }, { value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }
      ], defaultValue: 'draft' }
    ]
  },
  {
    id: 'linkedin_ads',
    label: 'LinkedIn Ads',
    description: 'B2B advertising for professional audiences',
    icon: 'Megaphone',
    category: 'ads',
    requiresUpload: false,
    mimeType: 'application/json',
    fields: [
      { name: 'campaign_name', label: 'Campaign Name', type: 'text', required: true, placeholder: 'Enter campaign name' },
      { name: 'ad_format', label: 'Ad Format', type: 'select', required: true, options: [
        { value: 'sponsored_content', label: 'Sponsored Content' }, { value: 'message_ads', label: 'Message Ads' },
        { value: 'dynamic_ads', label: 'Dynamic Ads' }, { value: 'text_ads', label: 'Text Ads' },
        { value: 'video_ads', label: 'Video Ads' }, { value: 'event_ads', label: 'Event Ads' }
      ]},
      { name: 'saved_audience', label: 'Use Saved Audiences (Optional)', type: 'multiselect', required: false, helpText: 'Select one or more saved audiences to auto-fill targeting - criteria will be combined', width: 'full', options: [] },
      { name: 'headline', label: 'Headline', type: 'text', required: true, placeholder: 'Ad headline', validation: { maxLength: 70 } },
      { name: 'intro_text', label: 'Intro Text', type: 'textarea', required: false, placeholder: 'Intro text', validation: { maxLength: 150 } },
      { name: 'description', label: 'Description', type: 'text', required: false, placeholder: 'Ad description', validation: { maxLength: 100 } },
      { name: 'ad_images', label: 'Ad Creative Images', type: 'file', required: false, helpText: 'Upload sponsored content images (1200x627px recommended, JPG/PNG)', width: 'full' },
      { name: 'call_to_action', label: 'Call to Action', type: 'select', required: false, options: [
        { value: 'learn_more', label: 'Learn More' }, { value: 'sign_up', label: 'Sign Up' }, { value: 'download', label: 'Download' },
        { value: 'apply', label: 'Apply' }, { value: 'register', label: 'Register' }
      ]},
      { name: 'link_url', label: 'Destination URL', type: 'url', required: true, placeholder: 'https://your-site.com' },
      { name: 'budget', label: 'Budget Amount', type: 'number', required: true, validation: { min: 10 } },
      { name: 'budget_type', label: 'Budget Type', type: 'select', required: true, options: [
        { value: 'daily', label: 'Daily' }, { value: 'total', label: 'Total Budget' }, { value: 'lifetime', label: 'Lifetime' }
      ]},
      { name: 'bid_strategy', label: 'Bid Strategy', type: 'select', required: false, options: [
        { value: 'maximum_delivery', label: 'Maximum Delivery' }, { value: 'cost_cap', label: 'Cost Cap' },
        { value: 'manual_bidding', label: 'Manual Bidding' }, { value: 'target_cost', label: 'Target Cost' }, { value: 'enhanced_cpc', label: 'Enhanced CPC' }
      ]},
      { name: 'start_date', label: 'Start Date', type: 'date', required: true },
      { name: 'end_date', label: 'End Date', type: 'date', required: false },
      { name: 'job_titles', label: 'Job Titles', type: 'tags', required: false, placeholder: 'CEO, CTO, Director...' },
      { name: 'job_functions', label: 'Job Functions', type: 'tags', required: false, placeholder: 'Engineering, Marketing...' },
      { name: 'seniorities', label: 'Seniority Levels', type: 'multiselect', required: false, options: [
        { value: 'entry', label: 'Entry Level' }, { value: 'manager', label: 'Manager' }, { value: 'director', label: 'Director' },
        { value: 'vp', label: 'VP' }, { value: 'cxo', label: 'C-Level' }, { value: 'owner', label: 'Owner/Partner' }
      ]},
      { name: 'industries', label: 'Industries', type: 'tags', required: false, placeholder: 'Technology, Finance...' },
      { name: 'company_sizes', label: 'Company Sizes', type: 'multiselect', required: false, options: [
        { value: '1-10', label: '1-10' }, { value: '11-50', label: '11-50' }, { value: '51-200', label: '51-200' },
        { value: '201-500', label: '201-500' }, { value: '501-1000', label: '501-1000' }, { value: '1001-5000', label: '1001-5000' },
        { value: '5001-10000', label: '5001-10000' }, { value: '10001+', label: '10001+' }
      ]},
      { name: 'companies', label: 'Target Companies', type: 'tags', required: false, placeholder: 'Specific company names' },
      { name: 'skills', label: 'Skills', type: 'tags', required: false, placeholder: 'Required skills' },
      { name: 'groups', label: 'LinkedIn Groups', type: 'tags', required: false, placeholder: 'Group names or IDs' },
      { name: 'locations', label: 'Locations', type: 'tags', required: true, placeholder: 'Countries/regions' },
      { name: 'conversation_starter', label: 'Conversation Starter (Message Ads)', type: 'textarea', required: false, placeholder: 'Opening message' },
      { name: 'lead_gen_form_id', label: 'Lead Gen Form ID', type: 'text', required: false, placeholder: 'Form ID' },
      { name: 'status', label: 'Status', type: 'select', required: false, options: [
        { value: 'draft', label: 'Draft' }, { value: 'pending', label: 'Pending' }, { value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }
      ], defaultValue: 'draft' }
    ]
  },
  {
    id: 'display_ads',
    label: 'Display Network',
    description: 'Programmatic display & banner advertising',
    icon: 'Image',
    category: 'ads',
    requiresUpload: false,
    mimeType: 'application/json',
    fields: [
      { name: 'campaign_name', label: 'Campaign Name', type: 'text', required: true, placeholder: 'Enter campaign name' },
      { name: 'ad_sizes', label: 'Ad Sizes', type: 'multiselect', required: true, options: [
        { value: '300x250', label: '300x250 (Medium Rectangle)' }, { value: '728x90', label: '728x90 (Leaderboard)' },
        { value: '160x600', label: '160x600 (Wide Skyscraper)' }, { value: '336x280', label: '336x280 (Large Rectangle)' },
        { value: '320x50', label: '320x50 (Mobile Banner)' }, { value: '970x250', label: '970x250 (Billboard)' },
        { value: '300x600', label: '300x600 (Half Page)' }, { value: 'responsive', label: 'Responsive' }
      ]},
      { name: 'saved_audience', label: 'Use Saved Audiences (Optional)', type: 'multiselect', required: false, helpText: 'Select one or more saved audiences to auto-fill targeting - criteria will be combined', width: 'full', options: [] },
      { name: 'banner_creatives', label: 'Banner Creative Files', type: 'file', required: false, helpText: 'Upload banner images for selected sizes (JPG, PNG, GIF, or HTML5 zip)', width: 'full' },
      { name: 'dsp', label: 'DSP Platform', type: 'select', required: true, options: [
        { value: 'google_display', label: 'Google Display Network' }, { value: 'amazon_dsp', label: 'Amazon DSP' },
        { value: 'trade_desk', label: 'The Trade Desk' }, { value: 'adobe', label: 'Adobe Advertising Cloud' }, { value: 'custom', label: 'Custom DSP' }
      ]},
      { name: 'inventory_types', label: 'Inventory Types', type: 'multiselect', required: true, options: [
        { value: 'web', label: 'Web' }, { value: 'app', label: 'In-App' }, { value: 'video', label: 'Video' }
      ]},
      { name: 'targeting_strategy', label: 'Targeting Strategy', type: 'select', required: true, options: [
        { value: 'contextual', label: 'Contextual' }, { value: 'behavioral', label: 'Behavioral' },
        { value: 'retargeting', label: 'Retargeting' }, { value: 'lookalike', label: 'Lookalike' }
      ]},
      { name: 'headline', label: 'Headline', type: 'text', required: false, placeholder: 'Display ad headline' },
      { name: 'call_to_action', label: 'Call to Action', type: 'text', required: false, placeholder: 'CTA text' },
      { name: 'budget', label: 'Budget Amount', type: 'number', required: true, validation: { min: 1 } },
      { name: 'budget_type', label: 'Budget Type', type: 'select', required: true, options: [
        { value: 'daily', label: 'Daily' }, { value: 'total', label: 'Campaign Total' }, { value: 'monthly', label: 'Monthly' }
      ]},
      { name: 'bid_strategy', label: 'Bid Strategy', type: 'select', required: true, options: [
        { value: 'cpm', label: 'CPM' }, { value: 'vcpm', label: 'vCPM' }, { value: 'cpc', label: 'CPC' }, { value: 'cpa', label: 'CPA' },
        { value: 'dynamic_cpm', label: 'Dynamic CPM' }, { value: 'header_bidding', label: 'Header Bidding' }
      ]},
      { name: 'start_date', label: 'Start Date', type: 'date', required: true },
      { name: 'end_date', label: 'End Date', type: 'date', required: false },
      { name: 'retargeting_lists', label: 'Retargeting Lists', type: 'tags', required: false, placeholder: 'Audience list IDs' },
      { name: 'contextual_categories', label: 'Contextual Categories', type: 'tags', required: false, placeholder: 'IAB categories' },
      { name: 'excluded_categories', label: 'Brand Safety - Excluded Categories', type: 'tags', required: true, placeholder: 'Categories to exclude' },
      { name: 'excluded_domains', label: 'Excluded Domains', type: 'tags', required: false, placeholder: 'Domains to exclude' },
      { name: 'viewability', label: 'Viewability Requirement', type: 'select', required: false, options: [
        { value: 'standard', label: 'Standard' }, { value: 'high', label: 'High (70%+)' }, { value: 'premium', label: 'Premium (90%+)' }
      ]},
      { name: 'frequency_cap', label: 'Frequency Cap', type: 'number', required: false, placeholder: 'Max impressions per user', validation: { min: 1 } },
      { name: 'frequency_period', label: 'Frequency Period', type: 'select', required: false, options: [
        { value: 'day', label: 'Per Day' }, { value: 'week', label: 'Per Week' }, { value: 'month', label: 'Per Month' }
      ]},
      { name: 'locations', label: 'Geographic Targets', type: 'tags', required: false, placeholder: 'Countries/regions' },
      { name: 'devices', label: 'Devices', type: 'multiselect', required: false, options: [
        { value: 'desktop', label: 'Desktop' }, { value: 'mobile', label: 'Mobile' }, { value: 'tablet', label: 'Tablet' }
      ]},
      { name: 'status', label: 'Status', type: 'select', required: false, options: [
        { value: 'draft', label: 'Draft' }, { value: 'pending', label: 'Pending' }, { value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }
      ], defaultValue: 'draft' }
    ]
  },
  {
    id: 'ctv_ads',
    label: 'Connected TV (CTV)',
    description: 'Streaming TV & OTT platform advertising',
    icon: 'Video',
    category: 'ads',
    requiresUpload: false,
    mimeType: 'application/json',
    fields: [
      { name: 'campaign_name', label: 'Campaign Name', type: 'text', required: true, placeholder: 'Enter campaign name' },
      { name: 'platforms', label: 'CTV Platforms', type: 'multiselect', required: true, options: [
        { value: 'roku', label: 'Roku' }, { value: 'amazon_fire', label: 'Amazon Fire TV' }, { value: 'apple_tv', label: 'Apple TV' },
        { value: 'samsung', label: 'Samsung TV' }, { value: 'hulu', label: 'Hulu' }, { value: 'youtube_tv', label: 'YouTube TV' },
        { value: 'pluto', label: 'Pluto TV' }
      ]},
      { name: 'video_resolution', label: 'Video Resolution', type: 'select', required: true, options: [
        { value: '1920x1080', label: '1920x1080 (Full HD)' }, { value: '1280x720', label: '1280x720 (HD)' }, { value: '3840x2160', label: '3840x2160 (4K)' }
      ]},
      { name: 'aspect_ratio', label: 'Aspect Ratio', type: 'select', required: true, options: [
        { value: '16:9', label: '16:9 (Standard)' }, { value: '4:3', label: '4:3' }, { value: '9:16', label: '9:16 (Vertical)' }
      ]},
      { name: 'video_duration', label: 'Video Duration', type: 'select', required: true, options: [
        { value: '15', label: '15 seconds' }, { value: '30', label: '30 seconds' }, { value: '60', label: '60 seconds' }
      ]},
      { name: 'video_format', label: 'Video Format', type: 'select', required: true, options: [
        { value: 'mp4', label: 'MP4' }, { value: 'mov', label: 'MOV' }, { value: 'webm', label: 'WebM' }
      ]},
      { name: 'video_creative', label: 'Video Creative File', type: 'file', required: false, helpText: 'Upload TV ad video (MP4, MOV, WebM - resolution specified above)', width: 'half' },
      { name: 'companion_banner', label: 'Companion Banner Image', type: 'file', required: false, helpText: 'Upload companion display banner (JPG, PNG)', width: 'half' },
      { name: 'headline', label: 'Headline', type: 'text', required: false, placeholder: 'Ad headline' },
      { name: 'call_to_action', label: 'Call to Action', type: 'text', required: false, placeholder: 'CTA text' },
      { name: 'budget', label: 'Budget Amount', type: 'number', required: true, validation: { min: 500 } },
      { name: 'budget_type', label: 'Budget Type', type: 'select', required: true, options: [
        { value: 'total', label: 'Campaign Total' }, { value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Quarterly' }
      ]},
      { name: 'bid_strategy', label: 'Bid Strategy', type: 'select', required: true, options: [
        { value: 'cpm', label: 'CPM' }, { value: 'cpcv', label: 'CPCV (Cost per Completed View)' },
        { value: 'fixed_rate', label: 'Fixed Rate' }, { value: 'programmatic_guaranteed', label: 'Programmatic Guaranteed' }
      ]},
      { name: 'start_date', label: 'Start Date', type: 'date', required: true },
      { name: 'end_date', label: 'End Date', type: 'date', required: false },
      { name: 'ad_pod_position', label: 'Ad Pod Position', type: 'select', required: false, options: [
        { value: 'first', label: 'First' }, { value: 'last', label: 'Last' }, { value: 'any', label: 'Any' }
      ]},
      { name: 'genres', label: 'Content Genres', type: 'tags', required: false, placeholder: 'Drama, Sports, News...' },
      { name: 'dayparts', label: 'Daypart Targeting', type: 'tags', required: false, placeholder: 'Prime Time, Late Night...' },
      { name: 'networks', label: 'TV Networks', type: 'tags', required: false, placeholder: 'Network names' },
      { name: 'shows', label: 'Specific Shows', type: 'tags', required: false, placeholder: 'Show names' },
      { name: 'household_income', label: 'Household Income', type: 'select', required: false, options: [
        { value: 'all', label: 'All' }, { value: 'under_50k', label: 'Under $50K' }, { value: '50k-100k', label: '$50K-$100K' },
        { value: '100k-150k', label: '$100K-$150K' }, { value: 'over_150k', label: 'Over $150K' }
      ]},
      { name: 'presence_of_children', label: 'Presence of Children', type: 'select', required: false, options: [
        { value: 'any', label: 'Any' }, { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }
      ]},
      { name: 'locations', label: 'Geographic Targets', type: 'tags', required: false, placeholder: 'DMA codes or regions' },
      { name: 'companion_banner_size', label: 'Companion Banner Size', type: 'select', required: false, options: [
        { value: '300x250', label: '300x250' }, { value: '728x90', label: '728x90' }, { value: '160x600', label: '160x600' }
      ]},
      { name: 'interactive_elements', label: 'Interactive Elements', type: 'select', required: false, options: [
        { value: 'no', label: 'No' }, { value: 'yes', label: 'Yes (QR codes, overlays)' }
      ]},
      { name: 'status', label: 'Status', type: 'select', required: false, options: [
        { value: 'draft', label: 'Draft' }, { value: 'pending', label: 'Pending' }, { value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }
      ], defaultValue: 'draft' }
    ]
  },
  {
    id: 'radio_ads',
    label: 'Radio & Audio Ads',
    description: 'Spotify, podcasts, internet & terrestrial radio',
    icon: 'Megaphone',
    category: 'ads',
    requiresUpload: false,
    mimeType: 'application/json',
    fields: [
      { name: 'campaign_name', label: 'Campaign Name', type: 'text', required: true, placeholder: 'Enter campaign name' },
      { name: 'platform', label: 'Audio Platform', type: 'select', required: true, options: [
        { value: 'spotify', label: 'Spotify' }, { value: 'pandora', label: 'Pandora' }, { value: 'iheartradio', label: 'iHeartRadio' },
        { value: 'podcast', label: 'Podcast Networks' }, { value: 'terrestrial_radio', label: 'Terrestrial Radio' }
      ]},
      { name: 'audio_duration', label: 'Audio Duration', type: 'select', required: true, options: [
        { value: '15', label: '15 seconds' }, { value: '30', label: '30 seconds' }, { value: '60', label: '60 seconds' }
      ]},
      { name: 'audio_format', label: 'Audio Format', type: 'select', required: true, options: [
        { value: 'mp3', label: 'MP3' }, { value: 'wav', label: 'WAV' }, { value: 'aac', label: 'AAC' }
      ]},
      { name: 'audio_bitrate', label: 'Audio Bitrate (kbps)', type: 'number', required: true, placeholder: '128-320', validation: { min: 128, max: 320 } },
      { name: 'audio_file', label: 'Audio Creative File', type: 'file', required: false, helpText: 'Upload audio ad (MP3, WAV, AAC - format/duration specified above)', width: 'half' },
      { name: 'companion_image', label: 'Companion Display Image', type: 'file', required: false, helpText: 'Upload Spotify canvas art or companion image (JPG, PNG)', width: 'half' },
      { name: 'voiceover_script', label: 'Voiceover Script', type: 'textarea', required: false, placeholder: 'Audio script text' },
      { name: 'headline', label: 'Campaign Headline', type: 'text', required: false, placeholder: 'Campaign headline' },
      { name: 'music_bed', label: 'Background Music', type: 'text', required: false, placeholder: 'Music description or track name' },
      { name: 'budget', label: 'Budget Amount', type: 'number', required: true, validation: { min: 100 } },
      { name: 'budget_type', label: 'Budget Type', type: 'select', required: true, options: [
        { value: 'total', label: 'Campaign Total' }, { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }
      ]},
      { name: 'bid_strategy', label: 'Bid Strategy', type: 'select', required: true, options: [
        { value: 'cpm', label: 'CPM' }, { value: 'cost_per_listen', label: 'Cost per Listen' },
        { value: 'fixed_sponsorship', label: 'Fixed Sponsorship' }, { value: 'dynamic_insertion', label: 'Dynamic Insertion' },
        { value: 'run_of_schedule', label: 'Run of Schedule' }
      ]},
      { name: 'start_date', label: 'Start Date', type: 'date', required: true },
      { name: 'end_date', label: 'End Date', type: 'date', required: false },
      { name: 'genres', label: 'Music Genres', type: 'tags', required: false, placeholder: 'Pop, Rock, Hip Hop...' },
      { name: 'playlists', label: 'Target Playlists', type: 'tags', required: false, placeholder: 'Playlist names' },
      { name: 'podcasts', label: 'Target Podcasts', type: 'tags', required: false, placeholder: 'Podcast names' },
      { name: 'dayparts', label: 'Daypart Targeting', type: 'tags', required: false, placeholder: 'Morning Drive, Evening...' },
      { name: 'markets', label: 'Radio Markets (DMA)', type: 'tags', required: false, placeholder: 'DMA codes for radio' },
      { name: 'devices', label: 'Devices', type: 'multiselect', required: false, options: [
        { value: 'mobile', label: 'Mobile' }, { value: 'desktop', label: 'Desktop' },
        { value: 'smart_speaker', label: 'Smart Speaker' }, { value: 'connected_car', label: 'Connected Car' }
      ]},
      { name: 'companion_display_url', label: 'Companion Display URL', type: 'url', required: false, placeholder: 'URL for companion ad' },
      { name: 'dynamic_insertion', label: 'Dynamic Ad Insertion', type: 'select', required: false, options: [
        { value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }
      ]},
      { name: 'status', label: 'Status', type: 'select', required: false, options: [
        { value: 'draft', label: 'Draft' }, { value: 'pending', label: 'Pending' }, { value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }
      ], defaultValue: 'draft' }
    ]
  },

  // Media assets (require file upload)
  {
    id: 'image',
    label: 'Image',
    description: 'Upload image files',
    icon: 'Image',
    category: 'media',
    requiresUpload: true,
    mimeType: 'image/*',
    fields: [
      {
        name: 'file',
        label: 'Image File',
        type: 'file',
        required: true,
        helpText: 'JPG, PNG, GIF, WebP (max 10MB)'
      },
      {
        name: 'alt_text',
        label: 'Alt Text',
        type: 'text',
        required: true,
        placeholder: 'Describe the image for accessibility'
      },
      {
        name: 'caption',
        label: 'Caption',
        type: 'text',
        required: false,
        placeholder: 'Optional image caption'
      },
      {
        name: 'tags',
        label: 'Tags',
        type: 'tags',
        required: false,
        placeholder: 'Add image tags'
      }
    ]
  },
  {
    id: 'video',
    label: 'Video',
    description: 'Upload video files',
    icon: 'Video',
    category: 'media',
    requiresUpload: true,
    mimeType: 'video/*',
    fields: [
      {
        name: 'file',
        label: 'Video File',
        type: 'file',
        required: true,
        helpText: 'MP4, WebM, MOV (max 100MB)'
      },
      {
        name: 'title',
        label: 'Video Title',
        type: 'text',
        required: true,
        placeholder: 'Enter video title'
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        required: false,
        placeholder: 'Describe the video content'
      },
      {
        name: 'transcript',
        label: 'Transcript',
        type: 'textarea',
        required: false,
        placeholder: 'Video transcript for accessibility'
      }
    ]
  },
  {
    id: 'pdf',
    label: 'PDF Document',
    description: 'Upload PDF documents',
    icon: 'FileText',
    category: 'media',
    requiresUpload: true,
    mimeType: 'application/pdf',
    fields: [
      {
        name: 'file',
        label: 'PDF File',
        type: 'file',
        required: true,
        helpText: 'PDF files (max 20MB)'
      },
      {
        name: 'title',
        label: 'Document Title',
        type: 'text',
        required: true,
        placeholder: 'Enter document title'
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        required: false,
        placeholder: 'Brief document description'
      }
    ]
  },
  {
    id: 'logo_contest',
    label: 'Logo Contest',
    description: 'Upload multiple logo variations for review and selection',
    icon: 'Image',
    category: 'media',
    requiresUpload: true,
    mimeType: 'image/*',
    fields: [
      {
        name: 'files',
        label: 'Logo Variations',
        type: 'file',
        required: true,
        helpText: 'Upload multiple logo design variations (PNG, SVG, JPG - transparent backgrounds recommended)'
      },
      {
        name: 'contest_name',
        label: 'Contest Name',
        type: 'text',
        required: true,
        placeholder: 'e.g., Spring 2024 Logo Redesign'
      },
      {
        name: 'description',
        label: 'Design Brief',
        type: 'textarea',
        required: false,
        placeholder: 'Describe the logo requirements, brand guidelines, or design direction...'
      },
      {
        name: 'color_palette',
        label: 'Color Palette',
        type: 'tags',
        required: false,
        placeholder: 'Add hex colors (e.g., #FF5733, #3498DB)'
      },
      {
        name: 'variations_count',
        label: 'Number of Variations',
        type: 'number',
        required: false,
        validation: { min: 2, max: 20 },
        placeholder: 'How many logo variations'
      },
      {
        name: 'usage_context',
        label: 'Usage Context',
        type: 'multiselect',
        required: false,
        options: [
          { value: 'web', label: 'Website' },
          { value: 'print', label: 'Print Materials' },
          { value: 'social', label: 'Social Media' },
          { value: 'merchandise', label: 'Merchandise' },
          { value: 'signage', label: 'Signage' }
        ]
      }
    ]
  }
];

export function getAssetTypeConfig(typeId: string): AssetTypeConfig | undefined {
  return assetTypes.find(type => type.id === typeId);
}

export function getAssetTypesByCategory(category: 'text' | 'media' | 'ads'): AssetTypeConfig[] {
  return assetTypes.filter(type => type.category === category);
}

export function generateFileName(assetType: string, customName?: string): string {
  const timestamp = Date.now();
  const config = getAssetTypeConfig(assetType);

  if (customName) {
    return customName;
  }

  const extension = config?.requiresUpload ? '' :
    config?.mimeType === 'application/json' ? '.json' :
    config?.mimeType === 'text/html' ? '.html' : '.txt';

  return `${assetType}_${timestamp}${extension}`;
}